import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Company, UserProfile } from '../../../shared/types';
import { convertDocumentTimestamps } from '@shared/utils/firestore';
import { UserLimitBadge } from '../components/UserLimitBadge';

interface CompanyDetailsData {
  company: Company | null;
  administrator: UserProfile | null;
  staffCount: number;
  managerCount: number;
}

export function CompanyDetails() {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<CompanyDetailsData>({
    company: null,
    administrator: null,
    staffCount: 0,
    managerCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!companyId) {
      setError('Company ID is required');
      setLoading(false);
      return;
    }

    const fetchCompanyDetails = async () => {
      try {
        setLoading(true);

        // Fetch company data
        const companyDoc = await getDoc(doc(db, 'Companies', companyId));
        if (!companyDoc.exists()) {
          setError('Company not found');
          setLoading(false);
          return;
        }

        const companyData = convertDocumentTimestamps({
          id: companyDoc.id,
          ...companyDoc.data(),
        }) as Company;

        // Fetch administrator data
        let administratorData: UserProfile | null = null;
        if (companyData.adminId) {
          const adminDoc = await getDoc(doc(db, 'Users', companyData.adminId));
          if (adminDoc.exists()) {
            administratorData = convertDocumentTimestamps({
              uid: adminDoc.id,
              ...adminDoc.data(),
            }) as UserProfile;
          }
        }

        // Fetch staff and manager counts
        const usersQuery = query(
          collection(db, 'Users'),
          where('companyId', '==', companyId)
        );
        const usersSnapshot = await getDocs(usersQuery);

        let staffCount = 0;
        let managerCount = 0;

        usersSnapshot.forEach((doc) => {
          const user = doc.data();
          if (user.userType === 'staff') staffCount++;
          if (user.userType === 'manager') managerCount++;
        });

        setData({
          company: companyData,
          administrator: administratorData,
          staffCount,
          managerCount,
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching company details:', err);
        setError('Failed to load company details');
        setLoading(false);
      }
    };

    fetchCompanyDetails();
  }, [companyId]);

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#6b7280' }}>Loading company details...</div>
      </div>
    );
  }

  if (error || !data.company) {
    return (
      <div style={{ padding: '40px' }}>
        <button
          onClick={() => navigate('/companies')}
          style={{
            marginBottom: '24px',
            padding: '8px 16px',
            backgroundColor: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            cursor: 'pointer',
          }}
        >
          ← Back to Companies
        </button>
        <div style={{ fontSize: '16px', color: '#ef4444' }}>{error || 'Company not found'}</div>
      </div>
    );
  }

  const { company, administrator, staffCount, managerCount } = data;

  return (
    <div style={{ padding: '32px' }}>
      {/* Back Button */}
      <button
        onClick={() => navigate('/companies')}
        style={{
          marginBottom: '24px',
          padding: '8px 16px',
          backgroundColor: '#f3f4f6',
          border: '1px solid #d1d5db',
          borderRadius: '6px',
          fontSize: '14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        ← Back to Companies
      </button>

      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px', color: '#111827' }}>
          {company.name}
        </h1>
        <p style={{ fontSize: '16px', color: '#6b7280' }}>
          Company Details and Information
        </p>
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px', marginBottom: '32px' }}>
        <div style={statCardStyle}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Total Staff</div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#111827' }}>{staffCount}</div>
        </div>
        <div style={statCardStyle}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Total Managers</div>
          <div style={{ fontSize: '32px', fontWeight: '700', color: '#111827' }}>{managerCount}</div>
        </div>
        <div style={statCardStyle}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>User Limit</div>
          <UserLimitBadge
            currentUsers={company.currentUsers}
            maxUsers={company.maxUsers}
            variant="large"
          />
        </div>
        <div style={statCardStyle}>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>Created</div>
          <div style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>
            {company.createdAt.toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Company Information */}
      <div style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: '#111827' }}>
          Company Information
        </h2>
        <div style={{ display: 'grid', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Company Name</div>
            <div style={{ fontSize: '15px', color: '#111827' }}>{company.name}</div>
          </div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Phone</div>
            <div style={{ fontSize: '15px', color: '#111827' }}>{company.phone || '-'}</div>
          </div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Address</div>
            <div style={{ fontSize: '15px', color: '#111827' }}>{company.address || '-'}</div>
          </div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Company ID</div>
            <div style={{ fontSize: '13px', color: '#6b7280', fontFamily: 'monospace' }}>{company.id}</div>
          </div>
        </div>
      </div>

      {/* Administrator Information */}
      <div style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: '#111827' }}>
          Company Administrator
        </h2>
        {administrator ? (
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Name</div>
              <div style={{ fontSize: '15px', color: '#111827' }}>
                {administrator.firstName} {administrator.lastName}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Email</div>
              <div style={{ fontSize: '15px', color: '#111827' }}>{administrator.email}</div>
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Phone</div>
              <div style={{ fontSize: '15px', color: '#111827' }}>
                {administrator.phoneNumber ? `${administrator.countryCode || ''} ${administrator.phoneNumber}` : '-'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Administrator ID</div>
              <div style={{ fontSize: '13px', color: '#6b7280', fontFamily: 'monospace' }}>{administrator.uid}</div>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '14px', color: '#6b7280' }}>No administrator found</div>
        )}
      </div>

      {/* Company Settings */}
      <div style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px', color: '#111827' }}>
          Company Settings
        </h2>
        <div style={{ display: 'grid', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>Grace Period</div>
            <div style={{ fontSize: '15px', color: '#111827' }}>
              {company.settings.gracePeriodMinutes} minutes
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const statCardStyle: React.CSSProperties = {
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  padding: '20px',
};
